// Copyright 2016 Sorama B.V.

import {
    MeasurementHealthIssue,
    Region,
    Resolution,
    StreamBeamformingProjection,
    StreamBeamformingProjectionProperties,
    StreamHologram,
    StreamHologramProperties,
    StreamSpectrum,
    StreamSpectrumProperties,
} from "datatypes/SoramaLocalApi";
import { Status } from "datatypes/Status";
import { ChannelSelection } from "services/ChannelSelection/ChannelSelection";
import { ChannelSelectionUsage } from "services/ChannelSelection/ChannelSelectionUsage";
import { ConnectedMeasurementDeviceSetup } from "services/Hardware/Datatypes/MeasurementDeviceSetup";
import { IMeasurementAcquisitionService } from "services/Measurement/IMeasurementAcquisitionService";
import { IMeasurementPreviewService } from "services/Measurement/IMeasurementPreviewService";

export abstract class IMeasurementManagementService
    implements IMeasurementAcquisitionService, IMeasurementPreviewService
{
    /**
     * Indicates if this is a low bandwidth mode measurement
     */
    public readonly lowBandwidthMode: boolean;

    /**
     * Indicates if this measurement is using a reference microphone
     */
    public readonly useReferenceMicrophone: boolean;

    /**
     * The currently active measurement setup
     */
    public readonly activeSetup: ConnectedMeasurementDeviceSetup;

    /**
     * Indicates whether the current 'activeSetup' is reserved
     */
    public readonly isReserved: Status;

    /**
     * Indicates whether the current 'activeSetup' is acquiring data
     */
    public readonly isAcquiring: Status;

    /**
     * Issues with the current measurement
     */
    public readonly issues: MeasurementHealthIssue[];

    /**
     * Set the provided setup as the active measurement setup and start reserving the devices in the setup.
     * On failure the service will keep attempting to connect to the devices.
     * @param setup Setup to set as active setup, may be null to release any devices currently reserved.
     */
    public abstract setActiveSetup(setup: ConnectedMeasurementDeviceSetup): void;

    /**
     * Set the provided channel selections for the given frontends of the active setup.
     * If a given frontend is not present in the active setup this will throw an error.
     * @param newChannelSelections A list with frontEndId's and ChannelSelections to assign to the frontends with those id's in the active setup.
     */
    public abstract setChannelSelections(newChannelSelections: {
        [frontEndId: string]: ChannelSelection | ChannelSelectionUsage;
    }): Promise<void>;

    /**
     * Set low bandwidth mode for this measurement, if a measurement is active this will reset the measurement
     * @param lowBandwidthMode Indicates whether to use low bandwidth mode or not
     */
    public abstract setLowBandwidthMode(lowBandwidthMode: boolean): void;

    /**
     * Set wether or not to use the reference microphone for the currently active setup
     * @param useReferenceMicrophone Set to true to also capture the reference microphone
     */
    public abstract setUseReferenceMicrophone(useReferenceMicrophone: boolean): void;

    /**
     * Start acquiring data using the selected setup, internal state will be set to measuring
     */
    public abstract startAcquisition(): Promise<void>;

    /**
     * Stop acquiring data using the selected setup, internal state will be set to idle
     */
    public abstract stopAcquisition(): Promise<void>;

    /**
     * Create a new recording
     * @param duration Duration of the recording
     * @param containerId (Optional) Id of the container to store the recording
     * @param soundProgressCallback (Optional) Callback method to call each time the recording recieves a sound progress report from the server
     * @param videoProgressCallback (Optional) Callback method to call each time the recording recieves a video progress report from the server
     * @returns Promise that completes when the recording is complete containing the id of the container were the recording is stored
     */
    public abstract record(
        duration: number,
        containerId?: string,
        soundProgressCallback?: (progress: number) => void,
        videoProgressCallback?: (progress: number) => void
    ): Promise<string>;

    //#endregion

    //#region IMeasurementPreviewService Members

    /**
     * Indicates whether the hologram preview function can be used
     */
    public isHologramAvailable: Status;

    /**
     * Gets a hologram in Pascal RMS with the given properties
     * Requires the 'isHologramAvailable' property to be 'Available'
     * @param properties Properties of the hologram to get
     */
    public abstract previewHologram(properties: StreamHologramProperties): Promise<StreamHologram>;

    /**
     * Indicates whether the beamforming projection preview function can be used
     */
    public isBeamformingProjectionAvailable: Status;

    /**
     * Gets a beamforming projection in Pascal RMS with the given properties
     * Requires the 'isBeamformingProjectionAvailable' property to be 'Available'
     * @param properties Properties of the beamforming projection to get
     */
    public abstract previewBeamformingProjection(
        properties: StreamBeamformingProjectionProperties
    ): Promise<StreamBeamformingProjection>;

    /**
     * Indicates whether the spectrum preview function can be used
     */
    public isSpectrumAvailable: Status;

    /**
     * Gets a audio spectrum in Pascal RMS
     * Requires the 'isSpectrumAvailable' property to be 'Available'
     */
    public abstract previewSpectrum(properties?: StreamSpectrumProperties): Promise<StreamSpectrum>;

    /**
     * Indicates whether the video preview functions can be used
     */
    public isVideoAvailable: Status;

    /**
     * Gets the resolution for the video device
     * Requires the 'isVideoAvailable' property to be 'Available'
     */
    public abstract getResolution(): Promise<Resolution>;

    /**
     * Gets a base64 encoded JPEG image from the video device
     * Requires the 'isVideoAvailable' property to be 'Available'
     */
    public abstract previewImage(): Promise<string>;

    /**
     * Gets the stream address for the video device
     * Requires the 'isVideoAvailable' property to be 'Available'
     */
    public abstract getStreamAddress(): Promise<string>;

    /**
     * Gets the visible region for the video device with respect to the center of the video device
     * Requires the 'isVideoAvailable' property to be 'Available'
     */
    public abstract getVisibleRegion(distance: number): Promise<Region>;

    //#endregion
}
